import React from "react";
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Box,
    Chip,
    IconButton,
    Tooltip,
    Button
} from "@mui/material";
import {
    Visibility as ViewIcon,
    Delete as DeleteIcon,
    Refresh as RefreshIcon,
    History as HistoryIcon,
    Language as DomainIcon,
    Schedule as ScheduleIcon,
    TrendingUp as PriceIcon
} from "@mui/icons-material";

const DomainsList = ({
    domains,
    loading,
    onSelectDomain,
    onRefresh,
    onDeleteDomain,
    onViewHistory,
    onConfigureSchedule,
    onViewPriceChanges
}) => {
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    };

    const handleDelete = (domain) => {
        if (window.confirm(`Delete all ${domain} products? This cannot be undone.`)) {
            onDeleteDomain(domain);
        }
    };

    if ((!domains || domains.length === 0) && !loading) {
        return (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
                <DomainIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
                <Typography variant="h6" color="textSecondary">
                    No websites scanned yet
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1, mb: 2 }}>
                    Click "New Scan" to start scraping your first ecommerce website
                </Typography>
            </Paper>
        );
    }

    return (
        <Paper>
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: 1, borderColor: 'divider' }}>
                <Box>
                    <Typography variant="h6">
                        Scanned Websites
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        {domains.length} domain{domains.length !== 1 ? 's' : ''} in database
                    </Typography>
                </Box>
                <Button
                    startIcon={<RefreshIcon />}
                    onClick={onRefresh}
                    variant="outlined"
                    disabled={loading}
                >
                    Refresh
                </Button>
            </Box>

            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Domain</TableCell>
                            <TableCell align="center">Products</TableCell>
                            <TableCell align="center">Brands</TableCell>
                            <TableCell>Last Scraped</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {domains && domains.map((domain) => (
                            <TableRow key={domain.domain} hover>
                                <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <DomainIcon color="action" />
                                        <Typography variant="body2" fontWeight="bold">
                                            {domain.domain}
                                        </Typography>
                                    </Box>
                                </TableCell>
                                
                                <TableCell align="center">
                                    <Chip
                                        label={domain.product_count}
                                        color="primary"
                                        size="small"
                                    />
                                </TableCell>
                                
                                <TableCell align="center">
                                    <Chip
                                        label={domain.brand_count || 0}
                                        size="small"
                                        variant="outlined"
                                    />
                                </TableCell>
                                
                                <TableCell>
                                    <Typography variant="body2">
                                        {formatDate(domain.last_scraped)}
                                    </Typography>
                                </TableCell>
                                
                                <TableCell align="right">
                                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                        <Tooltip title="View Products">
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={() => onSelectDomain(domain.domain)}
                                            >
                                                <ViewIcon />
                                            </IconButton>
                                        </Tooltip>
                                        
                                        <Tooltip title="View Price Changes">
                                            <IconButton
                                                size="small"
                                                color="secondary"
                                                onClick={() => onViewPriceChanges(domain.domain)}
                                            >
                                                <PriceIcon />
                                            </IconButton>
                                        </Tooltip>
                                        
                                        <Tooltip title="Configure Schedule">
                                            <IconButton
                                                size="small"
                                                onClick={() => onConfigureSchedule(domain.domain)}
                                            >
                                                <ScheduleIcon />
                                            </IconButton>
                                        </Tooltip>
                                        
                                        <Tooltip title="View Scan History">
                                            <IconButton
                                                size="small"
                                                onClick={() => onViewHistory(domain.domain)}
                                            >
                                                <HistoryIcon />
                                            </IconButton>
                                        </Tooltip>
                                        
                                        <Tooltip title="Delete All Products">
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => handleDelete(domain.domain)}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
};

export default DomainsList;
